import { dataManager } from '../APIEntity';
import { FSEntry } from '../fs/FSEntry';
import { ActionInfo, TypeId } from '../models';
import { DownloadOptions, UploadOptions } from '../models/FSOptions';
import { FileUpload } from './FileUpload';
import { ConnectionManager } from '../websocket';
import type { AssetDocument, DocumentPatch } from '../fs/AssetDocument';

/**
 * Heuristic: does a UTF-8-decoded string look like binary rather than text?
 *
 * Some drag-drop / clipboard sources hand us a File with an empty or missing
 * MIME type, which slips past a `file.type`-based check. Decoding such a binary
 * file (e.g. a JPEG) as UTF-8 yields a NUL byte and/or a high density of C0
 * control chars and U+FFFD replacement chars. Writing that as a text/markdown
 * body corrupts the file and locks up downstream editors, so we reject it.
 */
export function looksBinaryText(s: string): boolean {
  if (!s) return false;
  // Sniff a bounded prefix only — a binary-as-text file reveals itself (NUL /
  // control-char density) within the first few KB, so we never scan the whole
  // body (this runs on the editor's per-keystroke render path).
  const sample = s.length > 4096 ? s.slice(0, 4096) : s;
  if (sample.includes('\u0000')) return true;
  let suspicious = 0;
  for (let i = 0; i < sample.length; i++) {
    const c = sample.charCodeAt(i);
    // Allow tab (9), LF (10), CR (13); flag other C0 controls + the U+FFFD char.
    if (c < 9 || (c > 13 && c < 32) || c === 0xfffd) suspicious++;
  }
  return suspicious / sample.length > 0.1;
}

/**
 * BrowseResult - Result of a directory listing operation
 */
export interface BrowseResult {
  items: FSEntry[];
  path: string;
  totalSize: number;
  itemCount: number;
}

/**
 * SymlinkResolveResult - Result of resolving a symbolic link
 */
export interface SymlinkResolveResult {
  target: string;
  is_valid: boolean;
  original_path: string;
}

/**
 * FSManager - Centralized service for file system operations
 * Provides a consistent API for browse, upload, download, and delete operations
 * All methods throw errors on failure (consistent with SDK patterns)
 */
/** One watched file: its listeners, and whether the server has it registered. */
interface FileWatch {
  typeid: TypeId;
  path: string;
  listeners: Set<() => void>;
  registered: boolean;
  syncing: boolean;
}

export class FSManager {
  async ensureDocument(typeid: TypeId, path: string, assetTypeId: TypeId, spec: Record<string, import('../fs/AssetDocument').DocumentValue>): Promise<void> {
    const action = this.createFSAction(typeid, 'ensure_document', path, 'POST');
    action.bodyParameters = { typeid: assetTypeId.toString(), spec };
    await dataManager.callAction(action);
  }

  async readDocument(typeid: TypeId, path: string): Promise<AssetDocument> {
    return dataManager.callAction<undefined, AssetDocument>(this.createFSAction(typeid, 'document', path));
  }

  async updateDocument(typeid: TypeId, path: string, patch: DocumentPatch): Promise<AssetDocument> {
    const action = this.createFSAction(typeid, 'document', path, 'POST');
    action.bodyParameters = { ...patch };
    const saved = await dataManager.callAction<DocumentPatch, AssetDocument>(action);
    this.announceWrite();
    return saved;
  }

  // ---- write notices --------------------------------------------------------

  private writeListeners = new Set<() => void>();

  /**
   * Subscribe to "a file was written through this client" (``writeFile`` /
   * ``updateDocument`` settled). A write to an asset in a git checkout is
   * auto-committed server-side with no event of its own, so views of git state
   * (the footer push button) listen here to re-ask. Returns the unsubscribe.
   */
  onFileWritten(listener: () => void): () => void {
    this.writeListeners.add(listener);
    return () => { this.writeListeners.delete(listener); };
  }

  private announceWrite(): void {
    this.writeListeners.forEach((fn) => fn());
  }
  private static instance: FSManager;

  static getInstance(): FSManager {
    if (!FSManager.instance) {
      FSManager.instance = new FSManager();
    }
    return FSManager.instance;
  }

  // ---- file watches: fs/watch → file_changed_msg ---------------------------

  private fileWatches = new Map<string, FileWatch>();
  private fileWatchWired = false;
  /** Bumped on every (re)connect: a watch sent before it was for the old socket. */
  private fileWatchEpoch = 0;

  /**
   * Call `onChange` whenever the file changes on disk; returns the unwatch.
   *
   * The file counterpart of an entity watch: one server watch per (entity, path)
   * however many listeners share it, dropped with the last one, and registered
   * again on every reconnect (the server forgets a dropped socket's watches).
   * Nothing is refetched here — the listener decides what a change means.
   */
  watchFile(typeid: TypeId, path: string, onChange: () => void): () => void {
    this.wireFileWatch();
    const clean = path.replace(/^\/+/, '');
    const key = `${typeid.toString()}|${clean}`;
    let watch = this.fileWatches.get(key);
    if (!watch) {
      watch = { typeid, path: clean, listeners: new Set(), registered: false, syncing: false };
      this.fileWatches.set(key, watch);
    }
    watch.listeners.add(onChange);
    void this.syncFileWatch(key);
    return () => {
      watch.listeners.delete(onChange);
      void this.syncFileWatch(key);
    };
  }

  /**
   * Bring the server's watch in line with whether anyone listens — one request
   * at a time per file. Sent concurrently, an unmount's `unwatch` and the
   * remount's `watch` can reach the server in either order and leave it
   * unwatched while every listener waits (seen live on a remounting view).
   */
  private async syncFileWatch(key: string): Promise<void> {
    const watch = this.fileWatches.get(key);
    if (!watch || watch.syncing) return;
    watch.syncing = true;
    try {
      for (;;) {
        const wanted = watch.listeners.size > 0;
        if (wanted === watch.registered) break;
        const epoch = this.fileWatchEpoch;
        if (!(await this.postFileWatch(watch, wanted ? 'watch' : 'unwatch'))) break;
        // Reconnected while it was in flight: it named the old socket, so it
        // does not count — the loop sends it again for the new one.
        watch.registered = epoch === this.fileWatchEpoch ? wanted : false;
      }
    } finally {
      watch.syncing = false;
      if (watch.listeners.size === 0 && !watch.registered) this.fileWatches.delete(key);
    }
  }

  /** True when the server took it. Not connected → false; `on_open` re-syncs. */
  private async postFileWatch(watch: FileWatch, action: 'watch' | 'unwatch'): Promise<boolean> {
    const cm = ConnectionManager.getInstance();
    if (!cm.connected) return false;
    const info = this.createFSAction(watch.typeid, action, watch.path, 'POST');
    info.bodyParameters = { connection_id: cm.id };
    try {
      await dataManager.callAction(info);
      return true;
    } catch {
      // Best effort: a failed watch only means no live refresh until the next
      // reconnect; a failed unwatch is cleared server-side when the socket goes.
      return false;
    }
  }

  private wireFileWatch(): void {
    if (this.fileWatchWired) return;
    this.fileWatchWired = true;
    const cm = ConnectionManager.getInstance();
    // Matched on the path: the server may name the entity by id where the
    // client used a uname (`compute_node-@local`).
    cm.on('on_file_changed', (message: { path?: string }) => {
      for (const watch of this.fileWatches.values()) {
        if (watch.path === message.path) watch.listeners.forEach((listener) => listener());
      }
    });
    // A new socket has no watches server-side: register every file again.
    cm.on('on_open', () => {
      this.fileWatchEpoch += 1;
      for (const [key, watch] of this.fileWatches) {
        watch.registered = false;
        void this.syncFileWatch(key);
      }
    });
  }

  /**
   * Create ActionInfo for FS operations
   */
  private createFSAction(typeid: TypeId, action: string, path?: string, method: string = 'GET'): ActionInfo {
    const actionInfo = new ActionInfo('fs', typeid.type, typeid.id, method as any);

    // Build subpath: action/path (e.g., "browse", "browse/subdir", "download/file.txt")
    let subPath: string = action;
    if (path && path !== '/') {
      const cleanPath = path.replace(/^\/+/, '');
      if (cleanPath) {
        subPath = `${action}/${cleanPath}`;
      }
    }

    actionInfo.subpath = subPath;
    return actionInfo;
  }

  /**
   * List directory contents
   * @param typeid - Entity TypeId that owns the file system
   * @param path - Relative path within the entity's file system (default: "/")
   * @returns BrowseResult containing items
   * @throws Error if the operation fails
   */
  async listDirectory(typeid: TypeId, path: string = '/'): Promise<BrowseResult> {
    const actionInfo = this.createFSAction(typeid, 'browse', path, 'GET');
    const payload = await dataManager.callAction<undefined, unknown>(actionInfo);
    let rawItems: unknown[] = [];

    if (Array.isArray(payload)) {
      rawItems = payload;
    } else if (payload == null) {
      // Some backend paths can return null for empty directories; treat as empty list.
      rawItems = [];
    } else {
      console.warn('[FSManager] Unexpected browse payload; falling back to empty list:', payload);
      rawItems = [];
    }

    const fsItems = rawItems.map((item) => new FSEntry(item as any));
    const totalSize = fsItems.reduce((sum, item) => sum + (item.size || 0), 0);

    return {
      items: fsItems,
      path,
      totalSize,
      itemCount: fsItems.length,
    };
  }

  /**
   * Check if a file or directory exists
   * @param typeid - Entity TypeId
   * @param path - File path to check
   * @returns true if exists, false otherwise
   */
  async exists(typeid: TypeId, path: string): Promise<boolean> {
    try {
      const parentPath = path.substring(0, path.lastIndexOf('/')) || '/';
      const filename = path.substring(path.lastIndexOf('/') + 1);

      // If no filename (path is root or directory), try to list it
      if (!filename || path === '/') {
        await this.listDirectory(typeid, path);
        return true;
      }

      // Check if file exists in parent directory
      const parentBrowse = await this.listDirectory(typeid, parentPath);
      // FSEntry.name returns full relative path, extract just the name for comparison
      return parentBrowse.items.some((item) => {
        const itemName = item.name.split('/').pop() || item.name;
        return itemName === filename;
      });
    } catch {
      return false;
    }
  }

  /**
   * Read a file that may not exist — ONE request, and never a 404.
   *
   * Returns the content, or `null` when the file is not there. Use this instead of
   * `exists()` + `download()`: that pair costs two round trips on the common
   * (present) path, and `download()`-and-catch costs a browser-level
   * `Failed to load resource: 404` on the absent one, which no client-side handling
   * can suppress. Absence is a valid answer here, not an error.
   */
  async readIfExists(typeid: TypeId, path: string): Promise<string | null> {
    const res = await dataManager.callAction<void, { exists?: boolean; content?: string | null }>(
      this.createFSAction(typeid, 'read_optional', path, 'GET'),
    );
    return res?.exists ? (res.content ?? '') : null;
  }

  /**
   * Extract a local `.zip` to look inside it; returns the extracted folder's
   * machine path. The folder is a disposable temp copy (re-extracted after the
   * OS wipes temp) — nothing is written beside the zip.
   */
  async extractPreview(typeid: TypeId, path: string): Promise<string> {
    const res = await dataManager.callAction<void, { path: string }>(
      this.createFSAction(typeid, 'extract_preview', path, 'POST'),
    );
    return res.path;
  }

  /**
   * Get download URL for a file
   * @param typeid - Entity TypeId
   * @param path - File path
   * @returns URL for the file
   */
  getDownloadUrl(typeid: TypeId, path: string): string {
    return this.createFSAction(typeid, 'download', path, 'GET').fullActionUrl;
  }

  /**
   * URL that RENDERS a file in the browser, rather than downloading it.
   *
   * The distinction is `Content-Disposition`, and the reason to care is that
   * this url's tail is the file's own path. A page loaded from it resolves its
   * relative links and assets against its own folder — which is what a page
   * pasted into an iframe as `srcDoc` cannot do, having no url at all.
   *
   * @param typeid - Entity TypeId
   * @param path - File path
   * @returns URL the browser can render the file from
   */
  getServeUrl(typeid: TypeId, path: string): string {
    return this.createFSAction(typeid, 'serve', path, 'GET').fullActionUrl;
  }

  /**
   * Download a file as string or Blob
   * @param typeid - Entity TypeId
   * @param path - File path to download
   * @param options - Download options (encoding, asBlob, offset, size)
   * @returns File content as string or Blob
   * @throws Error if the download fails
   */
  async download(typeid: TypeId, path: string, options?: DownloadOptions): Promise<string | Blob> {
    const actionInfo = this.createFSAction(typeid, 'download', path, 'GET');
    actionInfo.isRawResponse = true;
    actionInfo.responseType = options?.asBlob ? 'blob' : 'text';

    const content = await dataManager.callAction<undefined, string | Blob>(actionInfo);
    return content;
  }

  /**
   * Download a file as a readable stream (for large files)
   * @param typeid - Entity TypeId
   * @param path - File path to download
   * @returns ReadableStream or null
   * @throws Error if streaming fails
   */
  async downloadAsStream(typeid: TypeId, path: string): Promise<ReadableStream<Uint8Array> | null> {
    const actionInfo = this.createFSAction(typeid, 'download', path, 'GET');
    actionInfo.isRawResponse = true;
    actionInfo.responseType = 'stream';

    const stream = await dataManager.callAction<undefined, ReadableStream<Uint8Array>>(actionInfo);
    return stream;
  }

  /**
   * Download directory as ZIP archive
   * @param typeid - Entity TypeId
   * @param path - Directory path to download (default: "/")
   * @returns Blob containing ZIP data
   * @throws Error if the download fails
   */
  async downloadZip(typeid: TypeId, path: string = '/'): Promise<Blob> {
    const actionInfo = this.createFSAction(typeid, 'download_zip', path, 'GET');
    actionInfo.isRawResponse = true;
    actionInfo.responseType = 'blob';

    const blob = await dataManager.callAction<undefined, Blob>(actionInfo);
    return blob;
  }

  /**
   * Upload a single file
   * @param typeid - Entity TypeId
   * @param destinationPath - Destination directory path (e.g., "/uploads/")
   * @param file - File to upload
   * @param options - Upload options (onProgress, encoding, overwrite)
   * @returns FileUpload object for tracking progress
   * @throws Error if the upload fails
   */
  async uploadFile(typeid: TypeId, destinationPath: string, file: File, options?: UploadOptions): Promise<FileUpload> {
    const uploads = await this.uploadFiles(typeid, destinationPath, [file], options);
    return uploads[0];
  }

  /**
   * Upload multiple files
   * @param typeid - Entity TypeId
   * @param destinationPath - Destination directory path
   * @param files - Array of files to upload
   * @param options - Upload options
   * @returns Array of FileUpload objects for tracking progress
   * @throws Error if the upload fails
   */
  async uploadFiles(
    typeid: TypeId,
    destinationPath: string,
    files: File[],
    options?: UploadOptions,
  ): Promise<FileUpload[]> {
    try {
      const formData = new FormData();

      files.forEach((file, index) => {
        formData.append(`file_${index}`, file, file.name);
      });

      const actionInfo = this.createFSAction(typeid, 'upload', destinationPath, 'POST');
      actionInfo.bodyParameters = formData;

      const uploadedItems = await dataManager.callAction<FormData, FSEntry[]>(actionInfo);
      const fsItems = uploadedItems.map((item) => new FSEntry(item));
      return this.createFileUploads(fsItems, options);
    } catch (error: any) {
      if (!this.shouldFallbackToWriteUpload(error)) {
        throw error;
      }

      const uploadedViaWrite: FSEntry[] = [];
      for (const file of files) {
        // Only fall back to text write for text files — binary files (images, etc.)
        // cannot be round-tripped through a UTF-8 text encoder without corruption.
        if (file.type && !file.type.startsWith('text/') && file.type !== 'application/json') {
          throw new Error(`Cannot upload binary file '${file.name}': server does not support multipart upload for this content type`);
        }
        const content = await this.readFileAsText(file);
        // A missing/empty MIME type (common for some drag-drop and clipboard
        // sources) slips past the check above, so also sniff the decoded bytes:
        // writing binary content as a text body corrupts the file and hangs the
        // editor that later opens it.
        if (looksBinaryText(content)) {
          throw new Error(`Cannot upload binary file '${file.name}': its contents are not text`);
        }
        const fullPath = this.joinDestinationPath(destinationPath, file.name);
        const writtenItem = await this.writeFile(typeid, fullPath, content);
        uploadedViaWrite.push(writtenItem);
      }

      return this.createFileUploads(uploadedViaWrite, options);
    }
  }

  private createFileUploads(fsEntries: FSEntry[], options?: UploadOptions): FileUpload[] {
    // The upload POST blocks until the bytes are stored, so a returned entry is
    // already durable. FSEntry is a value with no graph row to subscribe to, and
    // no UI renders a live percentage, so progress is a single 100% on
    // completion. (Live streaming progress, if ever needed, belongs on a
    // File-entity channel — see the FSItem removal notes.)
    return fsEntries.map((fsEntry) => {
      const fileUpload = new FileUpload(fsEntry);
      fileUpload._updateProgress(100);
      options?.onProgress?.(100, fsEntry.name);
      return fileUpload;
    });
  }

  private shouldFallbackToWriteUpload(error: unknown): boolean {
    const err = error as any;
    const message = String(err?.message || '').toLowerCase();
    const detail = String(err?.response?.data?.detail || '').toLowerCase();
    const serverMessage = String(err?.response?.data?.message || '').toLowerCase();
    return (
      message.includes('no files found in request') ||
      detail.includes('no files found in request') ||
      serverMessage.includes('no files found in request')
    );
  }

  private async readFileAsText(file: File): Promise<string> {
    if (typeof (file as any).text === 'function') {
      return (file as any).text();
    }

    if (typeof (file as any).arrayBuffer === 'function') {
      const buffer = await (file as any).arrayBuffer();
      return new TextDecoder().decode(buffer);
    }

    if (typeof FileReader !== 'undefined') {
      return await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(new Error(`Unable to read file content for ${file.name}`));
        reader.onload = () => resolve(String(reader.result ?? ''));
        reader.readAsText(file);
      });
    }

    throw new Error(`Unable to read file content for ${file.name}`);
  }

  private joinDestinationPath(destinationPath: string, filename: string): string {
    const normalizedDestination = destinationPath === '/' ? '' : destinationPath.replace(/\/+$/, '');
    const normalizedFilename = filename.replace(/^\/+/, '');
    return `${normalizedDestination}/${normalizedFilename}`.replace(/^\/+/, '');
  }

  /**
   * Upload a file from Blob
   * @param typeid - Entity TypeId
   * @param destinationPath - Destination directory path
   * @param blob - Blob to upload
   * @param filename - Name for the uploaded file
   * @param options - Upload options
   * @returns FileUpload object for tracking progress
   * @throws Error if the upload fails
   */
  async uploadFromBlob(
    typeid: TypeId,
    destinationPath: string,
    blob: Blob,
    filename: string,
    options?: UploadOptions,
  ): Promise<FileUpload> {
    const file = new File([blob], filename, { type: blob.type });
    return this.uploadFile(typeid, destinationPath, file, options);
  }

  /**
   * Upload and extract a ZIP file
   * @param typeid - Entity TypeId
   * @param destinationPath - Destination directory path
   * @param zipFile - ZIP file to upload
   * @returns Success message
   * @throws Error if the upload fails
   */
  async uploadZip(typeid: TypeId, destinationPath: string, zipFile: File): Promise<string> {
    const formData = new FormData();
    formData.append('file', zipFile, zipFile.name);

    const actionInfo = this.createFSAction(typeid, 'upload_zip', destinationPath, 'POST');
    actionInfo.bodyParameters = formData;

    return await dataManager.callAction<FormData, string>(actionInfo);
  }

  /**
   * Delete a file or directory
   * @param typeid - Entity TypeId
   * @param path - Path to delete
   * @returns void
   * @throws Error if the deletion fails
   */
  async delete(typeid: TypeId, path: string): Promise<void> {
    const actionInfo = this.createFSAction(typeid, 'delete', path, 'DELETE');
    await dataManager.callAction<undefined, any>(actionInfo);
  }

  /**
   * Rename a file or directory (same parent directory)
   * @param typeid - Entity TypeId
   * @param path - Current path of the file/directory
   * @param newName - New name for the file/directory (just the name, not full path)
   * @returns FSEntry of the renamed file
   * @throws Error if the rename fails
   */
  async rename(typeid: TypeId, path: string, newName: string): Promise<FSEntry> {
    // Validate newName doesn't contain path separators
    if (newName.includes('/')) {
      throw new Error('New name cannot contain path separators. Use move() for moving to different directories.');
    }

    const actionInfo = this.createFSAction(typeid, 'rename', path, 'POST');
    actionInfo.queryParameters = { new_name: newName };
    const item = await dataManager.callAction<undefined, FSEntry>(actionInfo);
    return new FSEntry(item);
  }

  /**
   * Copy a file or directory to a new location
   * @param typeid - Entity TypeId
   * @param sourcePath - Path to the source file/directory
   * @param destPath - Destination path (including filename)
   * @returns FSEntry of the copied file
   * @throws Error if the copy fails
   */
  async copy(typeid: TypeId, sourcePath: string, destPath: string): Promise<FSEntry> {
    // Validate destination path includes a filename
    if (destPath.endsWith('/')) {
      throw new Error('Destination path must include a filename');
    }

    const actionInfo = this.createFSAction(typeid, 'copy', sourcePath, 'POST');
    actionInfo.queryParameters = { dest_path: destPath };
    const item = await dataManager.callAction<undefined, FSEntry>(actionInfo);
    return new FSEntry(item);
  }

  /**
   * Move a file or directory to a new location
   * @param typeid - Entity TypeId
   * @param sourcePath - Path to the source file/directory
   * @param destPath - Destination path (including filename)
   * @returns FSEntry of the moved file
   * @throws Error if the move fails
   */
  async move(typeid: TypeId, sourcePath: string, destPath: string): Promise<FSEntry> {
    const actionInfo = this.createFSAction(typeid, 'move', sourcePath, 'POST');
    actionInfo.queryParameters = { dest_path: destPath };
    const item = await dataManager.callAction<undefined, FSEntry>(actionInfo);
    return new FSEntry(item);
  }

  /**
   * Create a new folder
   * @param typeid - Entity TypeId
   * @param path - Path for the new folder
   * @returns FSEntry of the created folder
   * @throws Error if the folder creation fails or folder already exists
   */
  async mkdir(typeid: TypeId, path: string): Promise<FSEntry> {
    const actionInfo = this.createFSAction(typeid, 'mkdir', path, 'POST');
    const item = await dataManager.callAction<undefined, FSEntry>(actionInfo);
    return new FSEntry(item);
  }

  /**
   * Write content directly to a file
   * @param typeid - Entity TypeId
   * @param path - File path to write to
   * @param content - String content to write
   * @returns FSEntry of the created/updated file
   * @throws Error if the write fails
   */
  async writeFile(typeid: TypeId, path: string, content: string): Promise<FSEntry> {
    const actionInfo = this.createFSAction(typeid, 'write', path, 'POST');
    actionInfo.bodyParameters = { content };
    const item = await dataManager.callAction<{ content: string }, FSEntry>(actionInfo);
    this.announceWrite();
    return new FSEntry(item);
  }

  /**
   * Open a file or folder in the OS default application
   * Only available in desktop and local environments
   * @param typeid - Entity TypeId
   * @param path - Path to the file or folder to open
   * @returns Success message
   * @throws Error if the open fails or environment doesn't support it
   */
  async open(typeid: TypeId, path: string): Promise<string> {
    const actionInfo = this.createFSAction(typeid, 'open', path, 'GET');
    return await dataManager.callAction<undefined, string>(actionInfo);
  }

  /**
   * Get file info (metadata) without downloading content
   * @param typeid - Entity TypeId
   * @param path - File path
   * @returns FSEntry or null if not found
   */
  async getInfo(typeid: TypeId, path: string): Promise<FSEntry | null> {
    try {
      const browseResult = await this.listDirectory(typeid, path);

      // If path is a directory with multiple items, return null
      if (browseResult.items.length > 1) {
        return null;
      }

      // Try to find the file in parent directory
      const parentPath = path.substring(0, path.lastIndexOf('/')) || '/';
      const filename = path.substring(path.lastIndexOf('/') + 1);

      if (!filename) {
        return null;
      }

      const parentBrowse = await this.listDirectory(typeid, parentPath);
      return parentBrowse.items.find((item) => item.name === filename) || null;
    } catch {
      return null;
    }
  }

  /**
   * Create a symbolic link
   * @param typeid - Entity TypeId
   * @param symlinkPath - Path where symlink will be created
   * @param targetPath - Path to target (VFS path or absolute)
   * @param relative - Create relative symlink (default: true)
   * @returns FSEntry of created symlink
   */
  async createSymlink(
    typeid: TypeId,
    symlinkPath: string,
    targetPath: string,
    relative: boolean = true,
  ): Promise<FSEntry> {
    if (symlinkPath.endsWith('/')) {
      throw new Error('Symlink path must include a filename');
    }

    const actionInfo = this.createFSAction(typeid, 'create_symlink', symlinkPath, 'POST');
    actionInfo.queryParameters = {
      target_path: targetPath,
      relative: relative.toString(),
    };

    const item = await dataManager.callAction<undefined, FSEntry>(actionInfo);
    return new FSEntry(item);
  }

  /**
   * Resolve a symbolic link to get its target
   * @param typeid - Entity TypeId
   * @param symlinkPath - Path to the symlink
   * @returns Object with target, validity, and original path
   */
  async resolveSymlink(typeid: TypeId, symlinkPath: string): Promise<SymlinkResolveResult> {
    const actionInfo = this.createFSAction(typeid, 'resolve_symlink', symlinkPath, 'GET');
    return await dataManager.callAction<undefined, SymlinkResolveResult>(actionInfo);
  }

  /**
   * Import an external folder or file by creating a symlink to it
   * This creates a symlink at the root of the entity's VFS pointing to an external path
   * @param typeid - Entity TypeId
   * @param externalPath - Absolute path to the external folder/file to import
   * @param linkName - Optional name for the symlink (defaults to basename of external path)
   * @returns FSEntry of the created symlink
   */
  async importItem(typeid: TypeId, externalPath: string, linkName?: string): Promise<FSEntry> {
    // Use empty path since symlink will be created at root
    const actionInfo = this.createFSAction(typeid, 'import_item', '', 'POST');
    actionInfo.queryParameters = {
      external_path: externalPath,
      ...(linkName && { link_name: linkName }),
    };

    const item = await dataManager.callAction<undefined, FSEntry>(actionInfo);
    return new FSEntry(item);
  }
}

// Export singleton instance
export const fsManager = FSManager.getInstance();

// Re-export FileUpload for convenience
export { FileUpload } from './FileUpload';
